import React, { useCallback, useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import CellContent from './CellContent';
import ContextMenuWrapper from './ContextMenuWrapper';
import IgnoredIcon from './IgnoredIcon';
import { AppContext } from '../../App';
import { MarkStateContext } from '../../context/MarkContext';
import { CellRendererProps, ContextMenuState } from './SpreadsheetTypes';
import { TFieldMeta } from '../../types/spreadsheet-types';

// Styled components

const DivCellWrapper = styled('div')({
  height: '100%',
  position: 'relative',
});

// Styles
const defaultStyles = {
  height: '100%',
};

const CellRenderer: React.FC<CellRendererProps> = (params) => {
  const [isCellHighlighted, setIsCellHighlighted] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({ x: 0, y: 0, visible: false });
  const { showValidations } = useContext(MarkStateContext);

  // Variables & Calculations
  const extractionId = params?.data.extractionId;
  const field = params?.colDef.field;
  const meta = params?.data?.fieldMeta?.[field] as TFieldMeta | undefined;
  const validationMessage = meta?.validationStatus ? meta.tooltip : null;
  const isErrorIgnored = !!meta?.ignored;
  const validating = !!params?.data?.validating;
  const validationNotReady = !!params?.data?.validationNotReady;

  // Handlers & Effects
  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, visible: true });
  };

  const closeContextMenu = useCallback(() => {
    setContextMenu((prevState) => ({ ...prevState, visible: false }));
  }, []);

  useEffect(() => {
    window.addEventListener('click', closeContextMenu);
    return () => {
      window.removeEventListener('click', closeContextMenu);
    };
  }, [closeContextMenu]);

  return (
    <>
      <DivCellWrapper
        style={{
          ...defaultStyles,
        }}
        onContextMenu={handleContextMenu}
      >
        <CellContent
          extractionId={extractionId}
          showRowValidationBadge={Boolean(
            params.column.getColDef().cellRendererParams?.showRowValidationBadge
          )}
          validationResponse={params.data.validationResponse}
          validating={validating}
          validationNotReady={validationNotReady}
          field={field}
          value={params.value}
        />
        {showValidations && isErrorIgnored && <IgnoredIcon field={field} />}
      </DivCellWrapper>
      {contextMenu.visible && (
        <ContextMenuWrapper
          extractionId={extractionId}
          isErrorIgnored={isErrorIgnored}
          isCellHighlighted={isCellHighlighted}
          setIsCellHighlighted={setIsCellHighlighted}
          validationMessage={validationMessage}
          contextMenu={contextMenu}
          closeContextMenu={closeContextMenu}
          field={field}
          cellValue={params.value}
          validationResponse={params.data.validationResponse}
          dpid={params.column.getColDef().cellRendererParams?.dpid}
          validating={validating}
          validationNotReady={validationNotReady}
        />
      )}
    </>
  );
};

export default CellRenderer;
